import React, { useEffect, useRef } from "react";
import { Pagination, PaginationProps } from "antd";
import { TestID } from "../../shared/testids/testids";
import "./COREPagination.less";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import classNames from "classnames";

interface CommonProps {
  testID: TestID;
  hideLastPageButton?: boolean;
}

export type COREPaginationProps = PaginationProps & CommonProps;

export const COREPagination: React.FC<COREPaginationProps> = ({
  size = "default",
  disabled = false,
  testID,
  defaultCurrent = 1,
  onChange,
  hideLastPageButton = false,
  ...props
}) => {
  const paginationRef = useRef<HTMLDivElement>(null);

  // Use useEffect to manipulate DOM after render when hideLastPageButton is true
  useEffect(() => {
    if (hideLastPageButton && paginationRef.current) {
      const hideLastPageElement = () => {
        const paginationContainer = paginationRef.current;
        if (paginationContainer) {
          // Find all pagination items that are page numbers (not prev/next/jump buttons)
          const pageItems = paginationContainer.querySelectorAll(
            ".ant-pagination-item:not(.ant-pagination-jump-prev):not(.ant-pagination-jump-next)"
          );

          // Hide the last page item
          if (pageItems.length > 0) {
            const lastPageItem = pageItems[pageItems.length - 1] as HTMLElement;
            lastPageItem.style.display = "none";
          }
        }
      };

      // Run immediately and also set up multiple timers to catch async updates
      hideLastPageElement();

      const timer1 = setTimeout(hideLastPageElement, 100);
      const timer2 = setTimeout(hideLastPageElement, 300); // Additional fallback

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [hideLastPageButton, props.total, props.pageSize]);

  const arrowRender = (current, type, originalElement) => {
    const classArr = classNames(size, "arrow-button", { disabled: disabled });

    if (type === "prev") {
      return (
        <button
          type="button"
          className={classNames(classArr, "arrow-button-prev")}
        >
          <CaretLeftOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </button>
      );
    }

    if (type === "next") {
      return (
        <button
          type="button"
          className={classNames(classArr, "arrow-button-next")}
        >
          <CaretRightOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </button>
      );
    }

    return originalElement;
  };

  return (
    <div ref={paginationRef} data-testid={testID} className="pagination">
      <Pagination
        size={size}
        disabled={disabled}
        defaultPageSize={20}
        itemRender={arrowRender}
        current={defaultCurrent}
        onChange={onChange}
        {...props}
      />
    </div>
  );
};
